// Colours are categorical data colours used to key each flag to its owning team in the list and
// legend. They are intentionally raw hex rather than design-system tokens: the tokens describe
// semantic UI roles (primary, success, danger) and carry no palette of mutually distinguishable
// category colours. That exemption is for this categorical palette only: surface, border, hover and
// progress colours are semantic roles and belong on tokens, so do not read this as licence to put
// chrome colours here or to add more hex alongside the components that draw these swatches.
// Exactly the ten teams the backend defines — nothing is added client-side. Every flag has a team
// set in the backend, so a placeholder team would only add a legend and filter entry that can never
// match a flag. UNKNOWN is not such a placeholder: it is a real value in the backend enum, used for
// flags whose owning team has not been decided, so it is an ordinary entry here like any other.
export type FeatureFlagTeamId =
	| 'PIKA'
	| 'CRUX'
	| 'INTEGRATIONS'
	| 'DARK_FACTORY'
	| 'IGNITE'
	| 'TENSOR'
	| 'ASTEROID'
	| 'ORBIT'
	| 'ARCHITECTURE'
	| 'UNKNOWN';

export interface FeatureFlagTeam {
	id: FeatureFlagTeamId;
	label: string;
	colour: string;
}

// Declaration order is load-bearing: it drives the legend order, the team filter's option order,
// and the primary sort key of the row list, so all three agree. UNKNOWN is last so unowned flags
// sort to the bottom, just ahead of a team this map does not recognise at all.
export const FEATURE_FLAG_TEAMS: readonly FeatureFlagTeam[] = [
	{id: 'PIKA', label: 'Pika', colour: '#0004db'},
	{id: 'CRUX', label: 'Crux', colour: '#148f6a'},
	{id: 'INTEGRATIONS', label: 'Integrations', colour: '#e57700'},
	{id: 'DARK_FACTORY', label: 'Dark Factory', colour: '#8a3800'},
	{id: 'IGNITE', label: 'Ignite', colour: '#e31672'},
	{id: 'TENSOR', label: 'Tensor', colour: '#005d5d'},
	{id: 'ASTEROID', label: 'Asteroid', colour: '#b28600'},
	{id: 'ORBIT', label: 'Orbit', colour: '#1192e8'},
	{id: 'ARCHITECTURE', label: 'Architecture', colour: '#a620a6'},
	{id: 'UNKNOWN', label: 'Unknown', colour: '#6f6f7c'},
];

/**
 * Drawn for a flag whose team this map does not know — i.e. a team added to the backend but not
 * yet given a colour here. Such a flag keeps the backend's raw team name and renders in grey, so a
 * new backend team degrades gracefully instead of disappearing or crashing. Distinct from the
 * UNKNOWN team above, which is a team the backend deliberately assigns.
 */
export const UNMAPPED_TEAM_COLOUR = '#acacb9';

const TEAMS_BY_ID = new Map<string, FeatureFlagTeam>(FEATURE_FLAG_TEAMS.map(team => [team.id, team]));

/** The matching team id, or null when the backend sends a team this map does not know. */
export function resolveTeamId(team: string | null | undefined): FeatureFlagTeamId | null {
	if (!team || !TEAMS_BY_ID.has(team)) {
		return null;
	}
	return team as FeatureFlagTeamId;
}

export function getTeam(id: FeatureFlagTeamId): FeatureFlagTeam {
	// Safe by construction: FeatureFlagTeamId only admits ids present in the map above.
	return TEAMS_BY_ID.get(id) as FeatureFlagTeam;
}

/**
 * Position of a team in the declared order — the primary sort key of the flag list. An unmapped
 * team sorts after every known team.
 */
export function getTeamOrder(id: FeatureFlagTeamId | null): number {
	if (id == null) {
		return FEATURE_FLAG_TEAMS.length;
	}
	const index = FEATURE_FLAG_TEAMS.findIndex(team => team.id === id);
	return index === -1 ? FEATURE_FLAG_TEAMS.length : index;
}
